import {
  IMyWorklistMetadataProps,
  useBuildingsShortQuery,
  useMyWorklistMetadataQuery,
  usePracticeShortQuery,
  useTaskFilterOptionsQuery,
  useTeamsShortQuery,
  useUserListShortQuery,
} from '@common/cache';
import { SelectChangeEvent } from '@mui/material';
import { ALL_OPTION } from '@common/types/constants';
import { IMyTasksFilters } from '../../../types';
import {
  useMyWorklistFilterStore,
  WorklistTabIndexType,
} from 'views/pages/Worklist/MyWorklist/store/filter.store.ts';

export type ICardFilters = Pick<
  IMyTasksFilters,
  | 'programs'
  | 'due_date'
  | 'category'
  | 'statuses'
  | 'location_building_ids'
  | 'priorities'
  | 'team_id'
  | 'practice_id'
  | 'assignee_id'
  | 'task_names'
  | 'order_types'
  | 'order_categories'
  | 'last_updated_after'
  | 'own_tasks_only'
>;

type Option<T> = {
  value: T;
  label: string;
  disabled?: boolean;
};

const getTaskFiltersMetadataFilters = (
  tabIndex: WorklistTabIndexType,
): Partial<IMyWorklistMetadataProps> => {
  switch (tabIndex) {
    case 'all':
      return {};
    case 'urgent':
      return {
        priorities: ['Overdue', 'Urgent'],
      };
    case 'inProgress':
      return {
        statuses: ['In progress'],
      };
    case 'notStarted':
      return {
        statuses: ['Pending'],
      };
    case 'completed':
      return {
        statuses: ['Completed'],
      };
    default:
      return {};
  }
};

export const useFiltersCard = () => {
  const {
    draftFilters,
    setDraftFilters,
    resetFilters,
    applyDraftFilters,
    handleSetIsFiltersCardClose,
    myWorklistTabIndex,
    tabIndex,
    appliedFiltersCount,
    isApplyEnabled,
    isResetEnabled,
  } = useMyWorklistFilterStore();
  const isUrgentTab = myWorklistTabIndex.urgent === tabIndex;
  const isCompletedTab = myWorklistTabIndex.completed === tabIndex;

  const metadata = useMyWorklistMetadataQuery({
    own_tasks_only: draftFilters.own_tasks_only,
    completed: isCompletedTab,
    ...getTaskFiltersMetadataFilters(tabIndex),
  });
  const buildingsQuery = useBuildingsShortQuery({});
  const { data: taskFilterOptions } = useTaskFilterOptionsQuery({
    completed: isCompletedTab,
  });
  const { data: teamData = [] } = useTeamsShortQuery({});

  const taskProgramOptions: Option<ICardFilters['programs'][number]>[] = [
    {
      value: 'GUIDE',
      label: `GUIDE (${metadata.data?.task_programs?.GUIDE ?? ' '})`,
    },
    {
      value: 'Pharmacy',
      label: `Pharmacy (${metadata.data?.task_programs?.Pharmacy ?? ' '})`,
    },
    {
      value: 'Optum MA',
      label: `Optum MA (${metadata.data?.task_programs?.['Optum MA'] ?? ' '})`,
    },
    {
      value: 'CCM',
      label: `CCM (${metadata.data?.task_programs?.['CCM'] ?? ' '})`,
    },
    {
      value: 'other',
      label: `Other (${metadata.data?.task_programs?.other ?? ' '})`,
    },
  ];

  const priorityOptions: Option<ICardFilters['priorities'][number]>[] = [
    {
      value: 'Overdue',
      label: `Overdue (${metadata.data?.priorities?.overdue ?? ' '})`,
    },
    {
      value: 'Urgent',
      label: `Urgent (${metadata.data?.priorities?.urgent ?? ' '})`,
    },
    {
      value: 'High',
      label: `High (${metadata.data?.priorities?.high ?? ' '})`,
      disabled: isUrgentTab,
    },
    {
      value: 'Medium',
      label: `Medium (${metadata.data?.priorities?.medium ?? ' '})`,
      disabled: isUrgentTab,
    },
    {
      value: 'Low',
      label: `Low (${metadata.data?.priorities?.low ?? ' '})`,
      disabled: isUrgentTab,
    },
  ].filter(item => !item.disabled) as Option<
    ICardFilters['priorities'][number]
  >[];

  const statusOptions: Option<ICardFilters['statuses'][number]>[] = [
    {
      value: 'Pending',
      label: `Not Started (${metadata.data?.statuses?.pending})`,
    },
    {
      value: 'In progress',
      label: `In progress (${metadata.data?.statuses?.in_progress})`,
    },
  ];

  const categoryOptions = metadata.data?.categories || [];

  const locationOptions =
    buildingsQuery.data?.map(building => ({
      value: building.id,
      label: building.name,
    })) || [];

  const { data: practiceData = [] } = usePracticeShortQuery({});
  const { data: userData = [] } = useUserListShortQuery({});

  const practiceOptions =
    practiceData.map(practice => ({
      value: practice.id,
      label: practice.name,
    })) || [];

  const teamOptions =
    teamData.map(team => ({
      value: team.id,
      label: team.name,
    })) || [];

  const userOptions =
    userData.map(user => ({
      value: user.id,
      label: user.name,
    })) || [];

  const handleProgramOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['programs'];
    setDraftFilters({
      ...draftFilters,
      programs: value,
    });
  };

  const handleStatusOnChange = (key: ICardFilters['statuses'][number] | '') => {
    if (key === ALL_OPTION.value) {
      setDraftFilters({
        ...draftFilters,
        statuses:
          statusOptions.length === draftFilters.statuses.length
            ? []
            : statusOptions.map(option => option.value),
      });
    } else {
      setDraftFilters({
        ...draftFilters,
        statuses: draftFilters.statuses.includes(key)
          ? draftFilters.statuses.filter(option => option !== key)
          : [...draftFilters.statuses, key],
      });
    }
  };

  const handlePriorityOnChange = (
    key: ICardFilters['priorities'][number] | '',
  ) => {
    if (key === ALL_OPTION.value) {
      setDraftFilters({
        ...draftFilters,
        priorities:
          priorityOptions.length === draftFilters.priorities.length
            ? []
            : priorityOptions.map(option => option.value),
      });
    } else {
      setDraftFilters({
        ...draftFilters,
        priorities: draftFilters.priorities.includes(key)
          ? draftFilters.priorities.filter(option => option !== key)
          : [...draftFilters.priorities, key],
      });
    }
  };

  const handleDueDateOnChange = (newDate: string) => {
    setDraftFilters({
      ...draftFilters,
      due_date: newDate,
    });
  };

  const handleCategoryOnChange = (e: SelectChangeEvent<string>) => {
    setDraftFilters({
      ...draftFilters,
      category: e.target.value,
    });
  };

  const handleLocationOnChange = (values: string[]) => {
    setDraftFilters({
      ...draftFilters,
      location_building_ids: values,
    });
  };

  const handlePracticeOnChange = (event: SelectChangeEvent<string>) => {
    setDraftFilters({
      ...draftFilters,
      practice_id: event.target.value,
    });
  };

  const handleTeamOnChange = (event: SelectChangeEvent<string>) => {
    setDraftFilters({
      ...draftFilters,
      team_id: event.target.value,
    });
  };

  const handleAssigneeOnChange = (event: SelectChangeEvent<string>) => {
    setDraftFilters({
      ...draftFilters,
      assignee_id: event.target.value,
    });
  };

  const handleTaskNameOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['task_names'];
    setDraftFilters({
      ...draftFilters,
      task_names: value,
    });
  };

  const handleOrderCategoryOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['order_categories'];
    setDraftFilters({
      ...draftFilters,
      order_categories: value,
    });
  };

  const handleOrderTypeOnChange = (newValues: string[]) => {
    const value = newValues as ICardFilters['order_types'];
    setDraftFilters({
      ...draftFilters,
      order_types: value,
    });
  };

  const handleLastUpdatedDateOnChange = (newDate: string) => {
    setDraftFilters({
      ...draftFilters,
      last_updated_after: newDate,
    });
  };

  const handleClearAll = () => {
    resetFilters();
  };

  const handleApply = () => {
    applyDraftFilters();
  };

  return {
    handleClearAll,
    handleApply,
    draftFilters,
    taskProgramOptions,
    priorityOptions,
    categoryOptions: categoryOptions.map(o => ({ value: o, label: o })),
    locationOptions,
    handleProgramOnChange,
    handleDueDateOnChange,
    handleCategoryOnChange,
    handleLocationOnChange,
    handlePriorityOnChange,
    handleCloseFiltersCard: handleSetIsFiltersCardClose,
    handlePracticeOnChange,
    handleAssigneeOnChange,
    handleTaskNameOnChange,
    handleOrderCategoryOnChange,
    handleOrderTypeOnChange,
    handleLastUpdatedDateOnChange,
    handleStatusOnChange,
    handleTeamOnChange,
    userOptions,
    practiceOptions,
    teamOptions,
    statusOptions,
    isCompletedTaskTabActive: myWorklistTabIndex.completed === tabIndex,
    hideStatusFilter:
      myWorklistTabIndex.notStarted === tabIndex ||
      myWorklistTabIndex.inProgress === tabIndex,
    isApplyEnabled,
    isResetEnabled,
    taskNameOptions: taskFilterOptions?.task_names.map(o => ({
      value: o,
      label: o,
    })),
    orderTypeOptions: taskFilterOptions?.order_types
      .filter(item => item !== 'Completed task')
      .map(o => ({
        value: o,
        label: o,
      })),
    orderCategoryOptions: taskFilterOptions?.order_categories.map(o => ({
      value: o,
      label: o,
    })),
    appliedFiltersCount,
    isCompletedTab,
  };
};
